import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, ExternalLink } from 'lucide-react';
import { TimeIntervalFilter } from '@/components/TimeIntervalFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { businessReportsApi } from '@/api/businessReports';
import { downloadBlob } from '@/lib/downloadBlob';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

function expenseProofLinks(
  row: {
    proofs?: Array<{ id: string; url: string; fileName: string }> | null;
    proofUrl?: string | null;
    proofFileName?: string | null;
  }
) {
  if (Array.isArray(row.proofs) && row.proofs.length > 0) {
    return row.proofs.filter((p) => p?.url);
  }
  if (row.proofUrl) {
    return [{ id: 'legacy', url: row.proofUrl, fileName: row.proofFileName || 'Invoice' }];
  }
  return [];
}

export default function CaExpenseReports() {
  const [serviceMonth, setServiceMonth] = useState('');
  const [exporting, setExporting] = useState(false);
  const {
    fromIso,
    toIso,
    selectedInterval,
    setSelectedInterval,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    handleTimeIntervalChange,
  } = useReportDateRange('this_month');

  const { data: report, isLoading } = useQuery({
    queryKey: ['ca-expense-report', fromIso, toIso, serviceMonth],
    queryFn: () =>
      businessReportsApi.expensesReport({
        from: fromIso!,
        to: toIso!,
        ...(serviceMonth ? { serviceMonth } : {}),
        includeLines: true,
      }),
    enabled: !!fromIso && !!toIso,
  });

  const dateSuffix =
    fromIso && toIso
      ? `${format(new Date(fromIso), 'yyyy-MM-dd')}-to-${format(new Date(toIso), 'yyyy-MM-dd')}`
      : 'report';

  const runExport = async () => {
    if (!fromIso || !toIso) return;
    setExporting(true);
    try {
      const resp = await businessReportsApi.expensesReportExport({
        from: fromIso,
        to: toIso,
        ...(serviceMonth ? { serviceMonth } : {}),
      });
      const smSuffix = serviceMonth ? `-${serviceMonth}` : '';
      downloadBlob(
        new Blob([resp.data], { type: 'text/csv;charset=utf-8' }),
        `Expense-Lines-${dateSuffix}${smSuffix}.csv`
      );
      toast.success('Download started');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const totals = report?.totals;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Expense reports</h2>
        <p className="text-slate-400">
          Approved and paid expenses (accrual basis). Filter by expense date and optional service month.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-4">
        <TimeIntervalFilter
          onTimeIntervalChange={handleTimeIntervalChange}
          selectedInterval={selectedInterval}
          onIntervalChange={setSelectedInterval}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label htmlFor="service-month" className="text-slate-400">
              Service month (optional)
            </Label>
            <Input
              id="service-month"
              type="month"
              value={serviceMonth}
              onChange={(e) => setServiceMonth(e.target.value)}
              className="w-[180px] bg-white/5 border-white/10 text-white"
            />
          </div>
          <Button
            variant="outline"
            className="border-white/15 bg-white/5 text-white hover:bg-white/10"
            disabled={!fromIso || !toIso || exporting}
            onClick={runExport}
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Exporting…' : 'Download detailed CSV'}
          </Button>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { label: 'IPD patient expenses', value: totals.ipdPatient },
            { label: 'Central expenses', value: totals.central },
            { label: 'Total expenses', value: totals.total },
            {
              label: 'Receptionist-entered',
              value: (totals.ipdReception ?? 0) + (totals.centralReception ?? 0),
            },
          ].map((t) => (
            <div key={t.label} className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs text-slate-400">{t.label}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-white">
                ₹{(t.value ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
        <div className="border-b border-white/10 px-4 py-3">
          <h3 className="font-medium text-white">Daily expense series</h3>
        </div>
        {isLoading ? (
          <p className="p-4 text-slate-400">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-right text-slate-400">IPD patient</TableHead>
                  <TableHead className="text-right text-slate-400">Central</TableHead>
                  <TableHead className="text-right text-slate-400">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.series ?? []).map((row) => (
                  <TableRow key={row.bucket} className="border-white/10">
                    <TableCell className="text-white">{row.bucket}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.ipdPatient.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.central.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{(row.total ?? row.ipdPatient + row.central).toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))}
                {(!report?.series || report.series.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                      No expense data for this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
        <div className="border-b border-white/10 px-4 py-3">
          <h3 className="font-medium text-white">Expense line items</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Patient and central expenses (approved/paid) with service month.
          </p>
        </div>
        {isLoading ? (
          <p className="p-4 text-slate-400">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Category</TableHead>
                  <TableHead className="text-slate-400">Service month</TableHead>
                  <TableHead className="text-slate-400">Vendor</TableHead>
                  <TableHead className="text-slate-400">Entered by</TableHead>
                  <TableHead className="text-right text-slate-400">Amount</TableHead>
                  <TableHead className="text-slate-400">Invoice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.lines ?? []).map((row, idx) => {
                  const proofs = expenseProofLinks(row);
                  return (
                  <TableRow key={`${row.expenseType}-${row.expenseId ?? row.expenseDate}-${idx}`} className="border-white/10">
                    <TableCell className="whitespace-nowrap text-white">
                      {format(new Date(row.expenseDate), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {row.expenseType === 'PATIENT' ? 'IPD patient' : 'Central'}
                    </TableCell>
                    <TableCell className="text-slate-300">{row.category}</TableCell>
                    <TableCell className="whitespace-nowrap text-slate-300">
                      {row.serviceMonth || '—'}
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate text-slate-300">
                      {row.vendorName || '—'}
                    </TableCell>
                    <TableCell className="max-w-[140px] truncate text-slate-300">
                      {row.createdByName || '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{Number(row.amount).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {proofs.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {proofs.map((p) => (
                            <a
                              key={p.id}
                              href={p.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-teal-300 underline-offset-4 hover:underline"
                            >
                              <ExternalLink className="h-3 w-3 shrink-0" />
                              <span className="truncate max-w-[120px]" title={p.fileName}>
                                {p.fileName || 'View'}
                              </span>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
                {(!report?.lines || report.lines.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                      No expense line items for this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
